import { cloneDeep } from 'lodash';
import moment from 'moment';

import { slugifyName } from 'bundles/common/utils/commonUtils';
import type {
  CourseMaterialsData,
  CourseMaterialsDataModuleLesson,
  CourseMaterialsDataModuleLessonItem,
} from 'bundles/in-course-stores/constants/CourseType';

import { ADAPTIVE_AI_GENERATED_ITEM_TYPE } from './constants';

export type AdaptiveLearningAiGeneratedMaterialItem = {
  id: string;
  name: string;
  /**
   * Specific adaptive-learning session-plan item type (e.g. `AI-GENERATED-ONE-PAGER`).
   * Stashed onto `contentSummary.definition.aiItemType` so downstream `Item` consumers can
   * distinguish the synthesized AI items without an extra context lookup.
   */
  aiItemType?: string;
};

const SYNTHETIC_LESSON_DISPLAY_NAME = 'AI generated';
const SYNTHETIC_LESSON_ID = '__coursera_adaptive_ai_synthetic_lesson__';

const createFakeAiGeneratedItem = (
  item: AdaptiveLearningAiGeneratedMaterialItem
): CourseMaterialsDataModuleLessonItem => ({
  name: item.name,
  slug: slugifyName(item.name),
  id: item.id,
  trackId: 'core',
  timeCommitment: moment.duration(0).asMilliseconds(),
  contentSummary: {
    typeName: ADAPTIVE_AI_GENERATED_ITEM_TYPE,
    definition: { aiItemType: item.aiItemType },
  } as unknown as CourseMaterialsDataModuleLessonItem['contentSummary'],
});

const createFakeAiGeneratedLesson = (
  items: AdaptiveLearningAiGeneratedMaterialItem[]
): CourseMaterialsDataModuleLesson => {
  const lessonItems = items.map((item) => createFakeAiGeneratedItem(item));
  return {
    id: SYNTHETIC_LESSON_ID,
    name: SYNTHETIC_LESSON_DISPLAY_NAME,
    slug: slugifyName(`${SYNTHETIC_LESSON_DISPLAY_NAME}-lesson`),
    trackId: 'core',
    timeCommitment: 0,
    itemGroups: [],
    elements: lessonItems,
  };
};

/**
 * Injects AI-generated adaptive learning items into a fake lesson
 * appended at the end of the course materials data.
 */
export const injectAdaptiveLearningAiGeneratedItems = (
  courseMaterialsData: CourseMaterialsData | undefined,
  aiGeneratedItems: AdaptiveLearningAiGeneratedMaterialItem[] = []
): CourseMaterialsData | undefined => {
  if (!courseMaterialsData?.elements?.length) {
    return courseMaterialsData;
  }

  if (aiGeneratedItems.length === 0) {
    return courseMaterialsData;
  }

  const result = cloneDeep(courseMaterialsData);
  const lastModule = result.elements[result.elements.length - 1];
  if (!lastModule) {
    return result;
  }

  const fakeAiGeneratedLesson = createFakeAiGeneratedLesson(aiGeneratedItems);
  lastModule.elements.push(fakeAiGeneratedLesson);

  return result;
};

/** Locates the synthetic AI lesson on an existing CourseMaterials instance, if it's been built. */
const getSyntheticAiLesson = (materials: $TSFixMe): $TSFixMe | undefined => {
  const modules = materials.getModules();
  const lastModule = modules.at(modules.length - 1);
  return lastModule?.getLessons().get(SYNTHETIC_LESSON_ID);
};

/** Re-wires the back-references new items need and busts the now-stale item caches. */
const reconcileSyntheticAiLesson = (materials: $TSFixMe, lesson: $TSFixMe) => {
  lesson.get('elements').invoke('set', {
    lesson,
    module: lesson.get('module'),
    course: lesson.get('course'),
    courseMaterials: lesson.get('courseMaterials'),
  });
  lesson.get('module').resetItemCaches();
  materials.resetItemCaches();
};

/**
 * Phase 1 of an in-place regen: adds the new AI items to the synthetic lesson on the existing
 * CourseMaterials instance while KEEPING the old ones alive, so the route's current item stays
 * resolvable. Returns false if the synthetic lesson hasn't been built yet (caller should do a
 * full rebuild instead).
 */
export const mergeAdaptiveLearningAiGeneratedItems = (
  materials: $TSFixMe,
  items: AdaptiveLearningAiGeneratedMaterialItem[]
): boolean => {
  const lesson = getSyntheticAiLesson(materials);
  if (!lesson) {
    return false;
  }

  const elements = lesson.get('elements');
  const existingIds = new Set(elements.map((el: $TSFixMe) => el.get('id')));
  const toAdd = items.filter((item) => !existingIds.has(item.id));
  if (toAdd.length > 0) {
    elements.add(toAdd.map(createFakeAiGeneratedItem), { parse: true });
    reconcileSyntheticAiLesson(materials, lesson);
  }
  return true;
};

/**
 * Phase 2: replaces the synthetic lesson's items with exactly `items`, pruning stale ones. Safe
 * once navigation has landed on a current item.
 */
export const replaceAdaptiveLearningAiGeneratedItems = (
  materials: $TSFixMe,
  items: AdaptiveLearningAiGeneratedMaterialItem[]
): void => {
  const lesson = getSyntheticAiLesson(materials);
  if (!lesson) {
    return;
  }

  lesson.get('elements').reset(items.map(createFakeAiGeneratedItem), { parse: true });
  reconcileSyntheticAiLesson(materials, lesson);
};
